import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Search, 
  Building2, 
  DollarSign, 
  Filter, 
  MapPin, 
  Grid3X3, 
  List, 
  Edit, 
  Users, 
  Eye,
  BarChart3,
  TrendingUp,
  PieChart,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
  Plus,
  UserPlus,
  Trash2,
  Phone,
  Mail,
  Contact,
  Building,
  Link,
  Unlink,
  Calendar,
  Clock,
  Target,
  TrendingDown,
  Loader
} from 'lucide-react';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useViewState } from '../utils/viewState';

const OfficeManagement = ({ selectedLocation = 'All Locations', userRole = 'admin' }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deskFilter, setDeskFilter] = useState('');
  
  // Persistent view state
  const [viewState, setViewState] = useViewState('officeManagement', { 
    viewMode: 'grid' 
  });
  const { viewMode } = viewState;
  const [offices, setOffices] = useState([]);
  const [locations, setLocations] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingOffice, setEditingOffice] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showAddOffice, setShowAddOffice] = useState(false);
  const [managingContacts, setManagingContacts] = useState(null);
  const [assigningMember, setAssigningMember] = useState(null);
  const [editingRates, setEditingRates] = useState(null);
  
  const { user } = useAuth();

  // Location details for display
  const locationDetails = {
    'Brooklyn': {
      address: '1 Metrotech Center, Brooklyn, NY 11201',
      manager: 'Sarah Johnson',
      floors: [2, 3, 4]
    },
    'Gowanus': {
      address: '61 9th St, Brooklyn, NY 11215',
      manager: 'Mike Chen',
      floors: [1, 2]
    },
    'Dumbo': {
      address: '81 Prospect St, Brooklyn, NY 11201',
      manager: 'Lisa Rodriguez',
      floors: [3, 4, 5]
    }
  };

  const [newOffice, setNewOffice] = useState({
    office_number: '',
    location_id: '',
    type: 'Private Office',
    desks: 1,
    floor: 1,
    windowed: false,
    marketed_rates: {
      monthly: 0,
      six_month: 0,
      twelve_month: 0
    }
  });

  const [newContact, setNewContact] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    is_primary: false
  });

  // Load data on component mount and when selectedLocation changes
  useEffect(() => {
    loadData();
  }, [selectedLocation]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load locations for dropdown
      const locationsData = await apiService.getLocations();
      setLocations(locationsData);

      // Load offices with location filter
      const filters = {};
      if (selectedLocation !== 'All Locations') {
        filters.location = selectedLocation;
      }
      const officesData = await apiService.getOffices(filters);
      setOffices(officesData);

      // Load members for assignment
      const membersData = await apiService.getMembers(filters);
      setMembers(membersData);

    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Get available members for assignment (filter by location if needed)
  const getAvailableMembers = () => {
    if (selectedLocation === 'All Locations') {
      return members;
    }
    return members.filter(member => member.location_name === selectedLocation);
  };

  // Get assigned member for an office
  const getAssignedMember = (officeId) => {
    return members.find(member => member.office_id === officeId);
  };

  // Get lease status
  const getLeaseStatus = (office) => {
    if (!office.lease_info?.start_date || !office.lease_info?.end_date) return 'No lease';
    
    const today = new Date();
    const start_date = new Date(office.lease_info.start_date);
    const end_date = new Date(office.lease_info.end_date);
    
    if (today < start_date) return 'Upcoming';
    if (today > end_date) return 'Expired';
    return 'Active';
  };

  // Get lease status color
  const getLeaseStatusColor = (office) => {
    const status = getLeaseStatus(office);
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-800 border-green-300';
      case 'Upcoming': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'Expired': return 'bg-red-100 text-red-800 border-red-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  // Get days until lease end
  const getDaysUntilLeaseEnd = (office) => {
    if (!office.lease_info?.end_date) return null;
    
    const today = new Date();
    const end_date = new Date(office.lease_info.end_date);
    const diffTime = end_date - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  // Filter offices based on search criteria (location filtering handled by API)
  const getFilteredOffices = () => {
    return offices.filter(office => {
      const assignedMember = getAssignedMember(office.id);
      const memberCompanyName = assignedMember?.company_name || '';
      
      const matchesSearch = office.office_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (office.tenant && office.tenant.toLowerCase().includes(searchTerm.toLowerCase())) ||
                           memberCompanyName.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || office.status === statusFilter;
      const matchesDesks = !deskFilter || office.desks.toString().includes(deskFilter);
      return matchesSearch && matchesStatus && matchesDesks;
    });
  };

  // Sort offices based on current sort configuration
  const getSortedOffices = () => {
    const filteredOffices = getFilteredOffices();
    
    if (!sortConfig.key) return filteredOffices;
    
    return [...filteredOffices].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      // Handle special cases
      if (sortConfig.key === 'signed_rate') {
        aValue = a.signed_rate || 0;
        bValue = b.signed_rate || 0;
      } else if (sortConfig.key === 'marketedRate') {
        aValue = a.marketed_rates?.monthly || 0;
        bValue = b.marketed_rates?.monthly || 0;
      } else if (sortConfig.key === 'tenant') {
        aValue = aValue || '';
        bValue = bValue || '';
      } else if (sortConfig.key === 'assignedMember') {
        const memberA = getAssignedMember(a.id);
        const memberB = getAssignedMember(b.id);
        aValue = memberA?.company_name || '';
        bValue = memberB?.company_name || '';
      } else if (sortConfig.key === 'leaseEndDate') {
        aValue = a.lease_info?.end_date || '';
        bValue = b.lease_info?.end_date || '';
      }
      
      // Convert to string for consistent comparison
      aValue = aValue.toString().toLowerCase();
      bValue = bValue.toString().toLowerCase();
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const sortedOffices = getSortedOffices();

  // Handle column sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sort icon for column headers
  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="w-4 h-4 ml-1 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' ? 
      <ChevronUp className="w-4 h-4 ml-1 text-slate-600" /> : 
      <ChevronDown className="w-4 h-4 ml-1 text-slate-600" />;
  };

  // API interaction functions
  const handleStatusChange = async (officeId, newStatus) => {
    try {
      await apiService.updateOfficeStatus(officeId, newStatus);
      await loadData(); // Reload data after update
    } catch (error) {
      console.error('Error updating office status:', error);
      setError('Failed to update office status');
    }
  };

  const handleAddOffice = async () => {
    try {
      if (!newOffice.office_number || !newOffice.location_id) {
        setError('Office number and location are required');
        return;
      }

      await apiService.createOffice(newOffice);
      setShowAddOffice(false);
      setNewOffice({
        office_number: '',
        location_id: '',
        type: 'Private Office',
        desks: 1,
        floor: 1,
        windowed: false,
        marketed_rates: {
          monthly: 0,
          six_month: 0,
          twelve_month: 0
        }
      });
      await loadData(); // Reload data after creation
    } catch (error) {
      console.error('Error creating office:', error);
      setError('Failed to create office');
    }
  };

  const handleAddContact = async () => {
    try {
      if (!newContact.name || !newContact.email || !managingContacts) {
        setError('Name and email are required');
        return;
      }

      await apiService.addOfficeContact(managingContacts, newContact);
      setNewContact({
        name: '',
        email: '',
        phone: '',
        role: '',
        is_primary: false
      });
      await loadData(); // Reload data after adding contact
    } catch (error) {
      console.error('Error adding contact:', error);
      setError('Failed to add contact');
    }
  };

  const handleRemoveContact = async (officeId, contactId) => {
    try {
      await apiService.removeOfficeContact(officeId, contactId);
      await loadData(); // Reload data after removing contact
    } catch (error) {
      console.error('Error removing contact:', error);
      setError('Failed to remove contact');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'LEASED': return 'bg-emerald-100 text-emerald-800 border-emerald-300';
      case 'VACANT': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'PENDING': return 'bg-amber-100 text-amber-800 border-amber-300';
      case 'UNAVAILABLE': return 'bg-red-100 text-red-800 border-red-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  // Note: updateOfficeStatus function removed - using handleStatusChange instead which uses API

  // Update office rates (API call)
  const updateOfficeRates = async (officeId, newRates, newLeaseInfo) => {
    try {
      await apiService.updateOffice(officeId, {
        signed_rate: newRates.signedRate,
        lease_info: newLeaseInfo
      });
      await loadData(); // Reload data after update
      setEditingRates(null);
    } catch (error) {
      console.error('Error updating office rates:', error);
      setError('Failed to update office rates');
    }
  };

  // Assign member to office (API call)
  const assignMemberToOffice = async (officeId, memberId) => {
    try {
      await apiService.assignMemberToOffice(memberId, officeId);
      await loadData(); // Reload data after assignment
      setAssigningMember(null);
    } catch (error) {
      console.error('Error assigning member:', error);
      setError('Failed to assign member to office');
    }
  };

  // Unassign member from office (API call would be needed)
  const unassignMemberFromOffice = async (officeId) => {
    try {
      // This would need a specific API endpoint to unassign
      // For now, we can set the office status to vacant
      await handleStatusChange(officeId, 'VACANT');
    } catch (error) {
      console.error('Error unassigning member:', error);
      setError('Failed to unassign member from office');
    }
  };

  // Note: addNewOffice function removed - using handleAddOffice instead which uses API

  // Note: Contact management functions removed - using handleAddContact and handleRemoveContact instead which use API

  const statusCounts = sortedOffices.reduce((acc, office) => {
    acc[office.status] = (acc[office.status] || 0) + 1;
    return acc;
  }, {});

  const totalDesks = sortedOffices.reduce((sum, office) => sum + office.desks, 0);
  const occupiedDesks = sortedOffices
    .filter(office => office.status === 'LEASED')
    .reduce((sum, office) => sum + office.desks, 0);
  const occupancyRate = totalDesks > 0 ? ((occupiedDesks / totalDesks) * 100).toFixed(1) : 0;

  // Get current revenue for an office
  const getCurrentRevenue = (office) => {
    return office.signed_rate || office.marketed_rates?.monthly || 0;
  };

  const totalRevenue = sortedOffices
    .filter(office => office.status === 'LEASED')
    .reduce((sum, office) => sum + getCurrentRevenue(office), 0);

  // Edit Rates Dialog Component
  const EditRatesDialog = ({ office, isOpen, onClose }) => {
    const [signedRate, setSignedRate] = useState(office?.signedRate || '');
    const [term_months, setTermMonths] = useState(office?.leaseInfo?.term_months || '');
    const [start_date, setStartDate] = useState(office?.leaseInfo?.start_date || '');
    
    if (!office) return null;

    const handleSave = () => {
      const end_date = start_date && term_months ? 
        new Date(new Date(start_date).setMonth(new Date(start_date).getMonth() + parseInt(term_months))).toISOString().split('T')[0] : 
        null;
        
      updateOfficeRates(office.id, { signedRate: parseFloat(signedRate) }, {
        term_months: term_months ? parseInt(term_months) : null,
        start_date: start_date || null,
        end_date
      });
    };

    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Rates & Lease Info - Office {office.office_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {/* Marketed Rates (Read Only) */}
            <div>
              <h4 className="font-medium text-slate-800 mb-3">Marketed Rates</h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg text-center">
                  <div className="text-sm text-gray-600">Monthly</div>
                  <div className="text-lg font-semibold">${office.marketed_rates?.monthly?.toLocaleString() || 'N/A'}</div>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg text-center">
                  <div className="text-sm text-blue-600">6 Months</div>
                  <div className="text-lg font-semibold text-blue-700">${office.marketed_rates?.six_month?.toLocaleString() || 'N/A'}</div>
                </div>
                <div className="p-3 bg-emerald-50 rounded-lg text-center">
                  <div className="text-sm text-emerald-600">12 Months</div>
                  <div className="text-lg font-semibold text-emerald-700">${office.marketed_rates?.twelve_month?.toLocaleString() || 'N/A'}</div>
                </div>
              </div>
            </div>

            {/* Signed Rate */}
            <div>
              <Label htmlFor="signedRate">Signed Rate (Monthly)</Label>
              <Input
                id="signedRate"
                type="number"
                value={signedRate}
                onChange={(e) => setSignedRate(e.target.value)}
                placeholder="Enter negotiated monthly rate"
              />
              <p className="text-xs text-gray-500 mt-1">
                The actual rate negotiated with the tenant (may differ from marketed rates)
              </p>
            </div>

            {/* Lease Information */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="term_months">Lease Term (Months)</Label>
                <Input
                  id="term_months"
                  type="number"
                  value={term_months}
                  onChange={(e) => setTermMonths(e.target.value)}
                  placeholder="e.g., 12"
                />
              </div>
              <div>
                <Label htmlFor="start_date">Lease Start Date</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={start_date}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
            </div>

            {/* Calculated End Date */}
            {start_date && term_months && (
              <div className="p-3 bg-blue-50 rounded-lg">
                <div className="text-sm text-blue-600">Calculated Lease End Date</div>
                <div className="font-semibold text-blue-800">
                  {new Date(new Date(start_date).setMonth(new Date(start_date).getMonth() + parseInt(term_months))).toLocaleDateString()}
                </div>
              </div>
            )}

            <div className="flex space-x-2">
              <Button onClick={handleSave} className="flex-1">
                Save Changes
              </Button>
              <Button variant="outline" onClick={onClose} className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  // Edit Status Dialog Component
  const EditStatusDialog = ({ office, isOpen, onClose }) => {
    const [newStatus, setNewStatus] = useState(office?.status || 'VACANT');
    
    if (!office) return null;

    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Office Status - {office.office_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Current Status</Label>
              <p className="text-sm text-gray-600">{office.status}</p>
            </div>
            <div>
              <Label htmlFor="status">New Status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="LEASED">LEASED</SelectItem>
                  <SelectItem value="VACANT">VACANT</SelectItem>
                  <SelectItem value="PENDING">PENDING</SelectItem>
                  <SelectItem value="UNAVAILABLE">UNAVAILABLE</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex space-x-2">
              <Button 
                onClick={async () => {
                  await handleStatusChange(office.id, newStatus);
                  onClose();
                }}
                className="flex-1"
              >
                Update Status
              </Button>
              <Button variant="outline" onClick={onClose} className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  // Add Office Dialog Component (Admin Only)
  const AddOfficeDialog = ({ isOpen, onClose }) => {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add New Office</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="officeNumber">Office Number</Label>
                <Input
                  id="officeNumber"
                  value={newOffice.officeNumber}
                  onChange={(e) => setNewOffice({...newOffice, officeNumber: e.target.value})}
                  placeholder="e.g., 2001"
                />
              </div>
              <div>
                <Label htmlFor="location">Location</Label>
                <Select 
                  value={newOffice.location} 
                  onValueChange={(value) => setNewOffice({...newOffice, location: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.slice(1).map((loc) => (
                      <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="desks">Number of Desks</Label>
                <Input
                  id="desks"
                  type="number"
                  value={newOffice.desks}
                  onChange={(e) => setNewOffice({...newOffice, desks: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="floor">Floor</Label>
                <Input
                  id="floor"
                  type="number"
                  value={newOffice.floor}
                  onChange={(e) => setNewOffice({...newOffice, floor: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="windowed">Windowed</Label>
                <Select 
                  value={newOffice.windowed ? 'true' : 'false'} 
                  onValueChange={(value) => setNewOffice({...newOffice, windowed: value === 'true'})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Yes</SelectItem>
                    <SelectItem value="false">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium">Marketed Pricing (Monthly rates)</Label>
              <div className="grid grid-cols-3 gap-4 mt-2">
                <div>
                  <Label htmlFor="monthlyRate" className="text-xs">1 Month Rate</Label>
                  <Input
                    id="monthlyRate"
                    type="number"
                    value={newOffice.marketed_rates.monthly}
                    onChange={(e) => setNewOffice({
                      ...newOffice, 
                      marketed_rates: {...newOffice.marketed_rates, monthly: e.target.value}
                    })}
                    placeholder="$"
                  />
                </div>
                <div>
                  <Label htmlFor="six_monthRate" className="text-xs">6 Month Rate</Label>
                  <Input
                    id="six_monthRate"
                    type="number"
                    value={newOffice.marketed_rates.six_month}
                    onChange={(e) => setNewOffice({
                      ...newOffice, 
                      marketed_rates: {...newOffice.marketed_rates, six_month: e.target.value}
                    })}
                    placeholder="$ (optional)"
                  />
                </div>
                <div>
                  <Label htmlFor="twelve_monthRate" className="text-xs">12 Month Rate</Label>
                  <Input
                    id="twelve_monthRate"
                    type="number"
                    value={newOffice.marketed_rates.twelve_month}
                    onChange={(e) => setNewOffice({
                      ...newOffice, 
                      marketed_rates: {...newOffice.marketed_rates, twelve_month: e.target.value}
                    })}
                    placeholder="$ (optional)"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                These are the rates displayed to prospects. Actual signed rates can be different.
              </p>
            </div>

            <div className="flex space-x-2">
              <Button onClick={handleAddOffice} className="flex-1">
                Add Office
              </Button>
              <Button variant="outline" onClick={onClose} className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  // Member Assignment Dialog Component
  const AssignMemberDialog = ({ office, isOpen, onClose }) => {
    const [selectedMemberId, setSelectedMemberId] = useState('');
    const availableMembers = getAvailableMembers();
    const currentAssignedMember = getAssignedMember(office?.id);
    
    if (!office) return null;

    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Assign Member Company - Office {office.office_number}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Current Assignment */}
            {currentAssignedMember && (
              <div className="p-4 border rounded-lg bg-gray-50">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-slate-800">Currently Assigned</h4>
                    <p className="text-lg font-semibold text-slate-800">{currentAssignedMember.company_name}</p>
                    <p className="text-sm text-gray-600">{currentAssignedMember.contact_name} • {currentAssignedMember.contact_email}</p>
                    <p className="text-sm text-gray-500">{currentAssignedMember.employees?.length || 0} employees</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => unassignMemberFromOffice(office.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Unlink className="w-4 h-4 mr-1" />
                    Unassign
                  </Button>
                </div>
              </div>
            )}

            {/* Available Members */}
            <div>
              <h4 className="font-medium text-slate-800 mb-3">
                {currentAssignedMember ? 'Change Assignment' : 'Select Member Company'}
              </h4>
              
              <Select value={selectedMemberId} onValueChange={setSelectedMemberId}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Choose a member company..." />
                </SelectTrigger>
                <SelectContent>
                  {availableMembers.map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      <div className="flex flex-col">
                        <span className="font-medium">{member.company_name}</span>
                        <span className="text-xs text-gray-500">
                          {member.contact_name} • {member.employees?.length || 0} employees • {member.membership_type}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {selectedMemberId && (
                <div className="mt-4">
                  {(() => {
                    const selectedMember = availableMembers.find(m => m.id === selectedMemberId);
                    return selectedMember ? (
                      <div className="p-3 border rounded-lg bg-blue-50">
                        <h5 className="font-medium text-blue-900">{selectedMember.company_name}</h5>
                        <p className="text-sm text-blue-700">{selectedMember.contact_name} • {selectedMember.contact_email}</p>
                        <p className="text-sm text-blue-600">
                          {selectedMember.employees?.length || 0} employees • {selectedMember.membership_type} membership
                        </p>
                      </div>
                    ) : null;
                  })()}
                </div>
              )}
            </div>
          </div>
          
          <div className="flex space-x-2 pt-4">
            <Button 
              onClick={() => assignMemberToOffice(office.id, selectedMemberId)}
              disabled={!selectedMemberId}
              className="flex-1"
            >
              <Link className="w-4 h-4 mr-2" />
              Assign Company
            </Button>
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  // Manage Contacts Dialog Component
  const ManageContactsDialog = ({ office, isOpen, onClose }) => {
    if (!office) return null;
    
    const contacts = office.contacts || [];

    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Manage Contacts - Office {office.office_number}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Existing Contacts */}
            <div>
              <h4 className="font-medium text-slate-800 mb-3">Current Contacts ({contacts.length})</h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {contacts.length > 0 ? (
                  contacts.map((contact) => (
                    <div key={contact.id} className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <p className="font-medium text-slate-800">{contact.name}</p>
                          {contact.isPrimary && (
                            <Badge variant="outline" className="text-xs">Primary</Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">{contact.email}</p>
                        {contact.phone && (
                          <p className="text-sm text-gray-600">{contact.phone}</p>
                        )}
                        {contact.role && (
                          <p className="text-xs text-gray-500">{contact.role}</p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveContact(office.id, contact.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-4">No contacts added yet</p>
                )}
              </div>
            </div>

            {/* Add New Contact */}
            <div className="border-t pt-4">
              <h4 className="font-medium text-slate-800 mb-3">Add New Contact</h4>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="contactName" className="text-sm">Name *</Label>
                    <Input
                      id="contactName"
                      value={newContact.name}
                      onChange={(e) => setNewContact({...newContact, name: e.target.value})}
                      placeholder="Contact name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contactEmail" className="text-sm">Email *</Label>
                    <Input
                      id="contactEmail"
                      type="email"
                      value={newContact.email}
                      onChange={(e) => setNewContact({...newContact, email: e.target.value})}
                      placeholder="email@company.com"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="contactPhone" className="text-sm">Phone</Label>
                    <Input
                      id="contactPhone"
                      value={newContact.phone}
                      onChange={(e) => setNewContact({...newContact, phone: e.target.value})}
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contactRole" className="text-sm">Role/Title</Label>
                    <Input
                      id="contactRole"
                      value={newContact.role}
                      onChange={(e) => setNewContact({...newContact, role: e.target.value})}
                      placeholder="e.g., Office Manager"
                    />
                  </div>
                </div>
                <Button 
                  onClick={handleAddContact}
                  className="w-full"
                  size="sm"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Contact
                </Button>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end pt-4">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-slate-600" />
            <p className="text-gray-600">Loading offices...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md mb-4">
          {error}
          <Button 
            variant="ghost" 
            size="sm" 
            className="ml-2 text-red-600 hover:text-red-700"
            onClick={() => setError(null)}
          >
            ×
          </Button>
        </div>
      )}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center space-x-3">
            <h1 className="text-3xl font-bold text-slate-800">Office Management</h1>
            {selectedLocation !== 'All Locations' && (
              <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-300">
                <MapPin className="w-3 h-3 mr-1" />
                {selectedLocation}
              </Badge>
            )}
          </div>
          <p className="text-gray-600 mt-2">
            {selectedLocation === 'All Locations' 
              ? 'Track office availability across all Bond Collective locations' 
              : `Track office availability and pricing for ${selectedLocation}`}
          </p>
        </div>
        
        <div className="flex items-center space-x-3">
          {/* Add Office Button (Admin Only) */}
          {userRole === 'admin' && (
            <Button
              onClick={() => setShowAddOffice(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Office
            </Button>
          )}
          
          {/* View Mode Toggle */}
          <div className="flex items-center space-x-2">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewState({ viewMode: 'grid' })}
            >
              <Grid3X3 className="w-4 h-4 mr-2" />
              Grid
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewState({ viewMode: 'list' })}
            >
              <List className="w-4 h-4 mr-2" />
              List
            </Button>
          </div>
        </div>
      </div>

      {/* Location Info Card */}
      {selectedLocation !== 'All Locations' && locationDetails[selectedLocation] && (
        <Card className="bg-gradient-to-r from-slate-50 to-gray-50 border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-800">{selectedLocation}</h3>
                <p className="text-gray-600">{locationDetails[selectedLocation].address}</p>
                <p className="text-sm text-gray-500 mt-1">
                  Manager: {locationDetails[selectedLocation].manager} • 
                  Floors: {locationDetails[selectedLocation].floors.join(', ')}
                </p>
              </div>
              <Building2 className="w-8 h-8 text-slate-600" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Occupancy Dashboard */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card className="border-gray-200">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-emerald-600">{statusCounts.LEASED || 0}</div>
            <div className="text-sm text-gray-600">Leased</div>
          </CardContent>
        </Card>
        <Card className="border-gray-200">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{statusCounts.VACANT || 0}</div>
            <div className="text-sm text-gray-600">Vacant</div>
          </CardContent>
        </Card>
        <Card className="border-gray-200">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-amber-600">{statusCounts.PENDING || 0}</div>
            <div className="text-sm text-gray-600">Pending</div>
          </CardContent>
        </Card>
        <Card className="border-gray-200">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-red-600">{statusCounts.UNAVAILABLE || 0}</div>
            <div className="text-sm text-gray-600">Unavailable</div>
          </CardContent>
        </Card>
        <Card className="border-gray-200 bg-gradient-to-br from-slate-50 to-gray-50">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-slate-700">{occupancyRate}%</div>
            <div className="text-sm text-gray-600">Occupancy</div>
          </CardContent>
        </Card>
        <Card className="border-gray-200 bg-gradient-to-br from-emerald-50 to-green-50">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-emerald-700">${totalRevenue.toLocaleString()}</div>
            <div className="text-sm text-gray-600">Monthly Revenue</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-gray-200">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search office, tenant, or company..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 border-gray-300"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="border-gray-300">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="LEASED">Leased</SelectItem>
                <SelectItem value="VACANT">Vacant</SelectItem>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="UNAVAILABLE">Unavailable</SelectItem>
              </SelectContent>
            </Select>
            <Input
              placeholder="Filter by desk count"
              value={deskFilter}
              onChange={(e) => setDeskFilter(e.target.value)}
              type="number"
              className="border-gray-300"
            />
            <div className="text-sm text-gray-600 flex items-center">
              <Users className="w-4 h-4 mr-1" />
              {totalDesks} total desks • {occupiedDesks} occupied
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Office Content */}
      {viewMode === 'grid' ? (
        /* Grid View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {sortedOffices.map((office) => {
            const assignedMember = getAssignedMember(office.id);
            const leaseStatus = getLeaseStatus(office);
            const daysUntilEnd = getDaysUntilLeaseEnd(office);
            return (
              <Card key={office.id} className="hover:shadow-lg transition-shadow duration-200 border-gray-200">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-slate-600" />
                      {office.type} {office.office_number}
                    </CardTitle>
                    <Badge className={getStatusColor(office.status)}>
                      {office.status}
                    </Badge>
                  </div>
                  {selectedLocation === 'All Locations' && (
                    <div className="flex items-center text-sm text-gray-600">
                      <MapPin className="w-3 h-3 mr-1" />
                      {office.location_name}
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-600">Desks:</span>
                        <p className="font-medium">{office.desks}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Floor:</span>
                        <p className="font-medium">{office.floor}</p>
                      </div>
                    </div>
                    
                    {/* Assigned Member Company */}
                    {assignedMember ? (
                      <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <Building className="w-4 h-4 mr-2 text-emerald-600" />
                            <span className="text-sm font-medium text-emerald-700">Assigned Company</span>
                          </div>
                        </div>
                        <p className="text-sm font-semibold text-emerald-800 mt-1">{assignedMember.company_name}</p>
                        <p className="text-xs text-emerald-600">{assignedMember.contact_name} • {assignedMember.employees?.length || 0} employees</p>
                      </div>
                    ) : (
                      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center">
                          <Building className="w-4 h-4 mr-2 text-gray-400" />
                          <span className="text-sm text-gray-500">No company assigned</span>
                        </div>
                      </div>
                    )}

                    {/* Lease Information */}
                    {office.lease_info?.start_date && (
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 mr-2 text-blue-600" />
                            <span className="text-sm font-medium text-blue-700">Lease Info</span>
                          </div>
                          <Badge className={getLeaseStatusColor(office)}>
                            {leaseStatus}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-blue-600">Term:</span>
                            <p className="font-medium text-blue-800">{office.lease_info.term_months} months</p>
                          </div>
                          <div>
                            <span className="text-blue-600">Start:</span>
                            <p className="font-medium text-blue-800">
                              {new Date(office.lease_info.start_date).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        {daysUntilEnd !== null && (
                          <div className="mt-2 text-xs">
                            <span className="text-blue-600">
                              {daysUntilEnd > 0 ? `${daysUntilEnd} days remaining` : 
                               daysUntilEnd === 0 ? 'Expires today' : 
                               `Expired ${Math.abs(daysUntilEnd)} days ago`}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Pricing Information */}
                    <div className="space-y-3 border-t border-gray-100 pt-3">
                      <div className="text-xs text-gray-500 text-center font-medium">Marketed Rates</div>
                      <div className="grid grid-cols-3 gap-1 text-xs">
                        <div className="text-center p-1.5 bg-blue-50 rounded">
                          <div className="text-blue-600 font-medium">Monthly</div>
                          <div className="font-semibold text-blue-800 text-[10px]">
                            ${office.marketed_rates?.monthly > 0 ? office.marketed_rates.monthly.toLocaleString() : 'N/A'}
                          </div>
                        </div>
                        <div className="text-center p-1.5 bg-amber-50 rounded">
                          <div className="text-amber-600 font-medium">6-Month</div>
                          <div className="font-semibold text-amber-800 text-[10px]">
                            ${office.marketed_rates?.six_month > 0 ? office.marketed_rates.six_month.toLocaleString() : 'N/A'}
                          </div>
                        </div>
                        <div className="text-center p-1.5 bg-emerald-50 rounded">
                          <div className="text-emerald-600 font-medium">12-Month</div>
                          <div className="font-semibold text-emerald-800 text-[10px]">
                            ${office.marketed_rates?.twelve_month > 0 ? office.marketed_rates.twelve_month.toLocaleString() : 'N/A'}
                          </div>
                        </div>
                      </div>
                      
                      {office.signed_rate && (
                        <div className="text-center p-2 bg-green-50 border border-green-200 rounded">
                          <div className="text-green-600 text-xs font-medium">Signed Rate</div>
                          <div className="font-bold text-green-700">
                            ${office.signed_rate.toLocaleString()}
                          </div>
                          {office.marketed_rates?.monthly && office.signed_rate !== office.marketed_rates.monthly && (
                            <div className="text-xs mt-1">
                              <span className={office.signed_rate < office.marketed_rates.monthly ? 'text-green-600' : 'text-red-600'}>
                                {office.signed_rate < office.marketed_rates.monthly ? '↓' : '↑'} 
                                ${Math.abs(office.signed_rate - office.marketed_rates.monthly)} vs monthly
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Contacts and Windowed Info */}
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center">
                        <Contact className="w-4 h-4 mr-1 text-gray-400" />
                        <span className="text-gray-600">
                          {(office.contacts || []).length} contact{(office.contacts || []).length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      {office.windowed !== undefined && (
                        <div className="flex items-center">
                          <Eye className="w-4 h-4 mr-1 text-gray-400" />
                          <span className="text-gray-600">
                            {office.windowed ? 'Windowed' : 'Interior'}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-5 gap-1 pt-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => navigate(`/offices/${office.id}`)}
                        title="View Profile"
                      >
                        <Eye className="w-3 h-3" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setEditingOffice(office)}
                      >
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setAssigningMember(office)}
                      >
                        <Building className="w-3 h-3" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setEditingRates(office)}
                      >
                        <Target className="w-3 h-3" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setManagingContacts(office)}
                      >
                        <Contact className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        /* List View */
        <Card className="border-gray-200">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th 
                      className="text-left p-4 text-sm font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('officeNumber')}
                    >
                      <div className="flex items-center">
                        Office
                        {getSortIcon('officeNumber')}
                      </div>
                    </th>
                    <th 
                      className="text-left p-4 text-sm font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center">
                        Status
                        {getSortIcon('status')}
                      </div>
                    </th>
                    <th 
                      className="text-left p-4 text-sm font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('assignedMember')}
                    >
                      <div className="flex items-center">
                        Assigned Company
                        {getSortIcon('assignedMember')}
                      </div>
                    </th>
                    <th 
                      className="text-left p-4 text-sm font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('desks')}
                    >
                      <div className="flex items-center">
                        Desks
                        {getSortIcon('desks')}
                      </div>
                    </th>
                    <th className="text-center p-4 text-sm font-medium text-gray-600">
                      Pricing Tiers
                      <div className="text-xs text-gray-500 mt-1">Monthly / 6M / 12M</div>
                    </th>
                    <th 
                      className="text-center p-4 text-sm font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('signed_rate')}
                    >
                      <div className="flex items-center justify-center">
                        Signed Rate
                        {getSortIcon('signed_rate')}
                      </div>
                    </th>
                    <th className="text-left p-4 text-sm font-medium text-gray-600">Lease Term</th>
                    <th 
                      className="text-left p-4 text-sm font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('leaseEndDate')}
                    >
                      <div className="flex items-center">
                        Lease End
                        {getSortIcon('leaseEndDate')}
                      </div>
                    </th>
                    <th className="text-left p-4 text-sm font-medium text-gray-600">Contacts</th>
                    {selectedLocation === 'All Locations' && (
                      <th 
                        className="text-left p-4 text-sm font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('location')}
                      >
                        <div className="flex items-center">
                          Location
                          {getSortIcon('location')}
                        </div>
                      </th>
                    )}
                    <th className="text-left p-4 text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedOffices.map((office) => {
                    const assignedMember = getAssignedMember(office.id);
                    const leaseStatus = getLeaseStatus(office);
                    return (
                      <tr key={office.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="p-4 font-medium text-slate-800">{office.office_number}</td>
                        <td className="p-4">
                          <Badge className={getStatusColor(office.status)}>
                            {office.status}
                          </Badge>
                        </td>
                        <td className="p-4">
                          {assignedMember ? (
                            <div>
                              <p className="font-medium text-slate-800">{assignedMember.company_name}</p>
                              <p className="text-xs text-gray-500">{assignedMember.contact_name}</p>
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="p-4 text-center font-medium">{office.desks}</td>
                        <td className="p-4">
                          <div className="flex items-center justify-center space-x-2 text-xs">
                            <div className="text-center">
                              <div className="text-blue-600 font-medium">${office.marketed_rates?.monthly > 0 ? office.marketed_rates.monthly.toLocaleString() : 'N/A'}</div>
                            </div>
                            <span className="text-gray-300">/</span>
                            <div className="text-center">
                              <div className="text-amber-600 font-medium">${office.marketed_rates?.six_month > 0 ? office.marketed_rates.six_month.toLocaleString() : 'N/A'}</div>
                            </div>
                            <span className="text-gray-300">/</span>
                            <div className="text-center">
                              <div className="text-emerald-600 font-medium">${office.marketed_rates?.twelve_month > 0 ? office.marketed_rates.twelve_month.toLocaleString() : 'N/A'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="p-4 text-center">
                          {office.signed_rate > 0 ? (
                            <div className="inline-block px-2 py-1 bg-green-50 border border-green-200 rounded text-green-700 font-semibold">
                              ${office.signed_rate.toLocaleString()}
                            </div>
                          ) : (
                            <span className="text-gray-400">N/A</span>
                          )}
                        </td>
                        <td className="p-4">
                          {office.lease_info?.term_months ? (
                            <div className="text-center">
                              <Badge className={getLeaseStatusColor(office)} variant="outline">
                                {office.lease_info.term_months}m
                              </Badge>
                              <div className="text-xs text-gray-500 mt-1">{leaseStatus}</div>
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="p-4 text-center">
                          {office.lease_info?.end_date ? (
                            <div>
                              <div className="font-medium text-slate-800">
                                {new Date(office.lease_info.end_date).toLocaleDateString()}
                              </div>
                              {(() => {
                                const days = getDaysUntilLeaseEnd(office);
                                return days !== null ? (
                                  <div className={`text-xs ${days <= 30 ? 'text-red-600' : days <= 90 ? 'text-amber-600' : 'text-gray-500'}`}>
                                    {days > 0 ? `${days}d left` : days === 0 ? 'Today' : `${Math.abs(days)}d ago`}
                                  </div>
                                ) : null;
                              })()}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="p-4 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setManagingContacts(office)}
                          >
                            {(office.contacts || []).length}
                          </Button>
                        </td>
                        {selectedLocation === 'All Locations' && (
                          <td className="p-4 text-gray-600">{office.location_name}</td>
                        )}
                        <td className="p-4">
                          <div className="flex space-x-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => navigate(`/offices/${office.id}`)}
                              title="View Profile"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => setEditingOffice(office)}
                              title="Edit Status"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => setAssigningMember(office)}
                              title="Assign Company"
                            >
                              <Building className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => setEditingRates(office)}
                              title="Edit Rates & Lease"
                            >
                              <Target className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => setManagingContacts(office)}
                              title="Manage Contacts"
                            >
                              <Contact className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {sortedOffices.length === 0 && (
        <Card className="border-gray-200">
          <CardContent className="p-12 text-center">
            <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No offices found matching your criteria.</p>
          </CardContent>
        </Card>
      )}

      {/* Dialogs */}
      <EditStatusDialog 
        office={editingOffice}
        isOpen={!!editingOffice}
        onClose={() => setEditingOffice(null)}
      />

      <EditRatesDialog 
        office={editingRates}
        isOpen={!!editingRates}
        onClose={() => setEditingRates(null)}
      />

      <AddOfficeDialog 
        isOpen={showAddOffice}
        onClose={() => setShowAddOffice(false)}
      />

      <AssignMemberDialog 
        office={assigningMember}
        isOpen={!!assigningMember}
        onClose={() => setAssigningMember(null)}
      />

      <ManageContactsDialog 
        office={managingContacts}
        isOpen={!!managingContacts}
        onClose={() => setManagingContacts(null)}
      />
    </div>
  );
};

export default OfficeManagement;